import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Container,
  TextField,
  Alert,
  CircularProgress,
  Link,
  useTheme,
  alpha,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import SmsIcon from '@mui/icons-material/Sms';
import loginBackground from '../assets/login-background.jpg';
import logoWhiteText from '../assets/morgan-rural-tech-logo.png';
import logoBlueText from '../assets/morgan-rural-tech-logo-blue.png';
import { useThemeMode } from '../contexts/ThemeModeContext';

const Login: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useThemeMode();
  const logo = mode === 'light' ? logoBlueText : logoWhiteText;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Set once password verification succeeds but SMS MFA is still required —
  // switches the form to the code-entry step instead of navigating in.
  const [mfaUserId, setMfaUserId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await auth.login(email, password);
      if (result.requiresMfa && result.userId) {
        setMfaUserId(result.userId);
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaUserId) return;
    setError('');
    setIsLoading(true);

    try {
      await auth.verifyMfa(mfaUserId, mfaCode);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid or expired code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Background photo with a slow Ken Burns zoom-out, matching the site's header animation */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${loginBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          animation: 'loginBgZoomOut 22s ease-out forwards',
          '@keyframes loginBgZoomOut': {
            from: { transform: 'scale(1.15)' },
            to: { transform: 'scale(1)' },
          },
        }}
      />
      {/* Darken + brand-tint overlay so the form stays readable over the photo */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(15,17,23,0.72)',
          backgroundImage: `radial-gradient(ellipse at 50% 0%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 60%)`,
        }}
      />
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              padding: { xs: 3, md: 5 },
              width: '100%',
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <img src={logo} alt="Morgan Rural Tech" style={{ height: 36 }} />
              </Box>
              <Typography
                component="h1"
                variant="h4"
                gutterBottom
                sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center', letterSpacing: '0.03em' }}
              >
                DMOTRAK
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', textAlign: 'center', mb: 4 }}
              >
                {['Device', 'Monitoring', '&', 'Object', 'Recognition'].map((word, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && ' '}
                    {word === '&' ? (
                      word
                    ) : (
                      <>
                        <Box component="span" sx={{ fontWeight: 700 }}>{word[0]}</Box>
                        <Box component="span" sx={{ fontWeight: 400 }}>{word.slice(1)}</Box>
                      </>
                    )}
                  </React.Fragment>
                ))}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              {mfaUserId ? (
                <form onSubmit={handleVerifyMfa} style={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                      We texted a verification code to your phone. Enter it below to finish signing in.
                    </Typography>
                    <TextField
                      fullWidth
                      label="Verification Code"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      placeholder="6-digit code"
                      disabled={isLoading}
                      autoFocus
                      required
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      type="submit"
                      disabled={isLoading || !mfaCode}
                      sx={{ py: 1.5, fontWeight: 600, fontSize: '1rem', '&:disabled': { opacity: 0.6 } }}
                    >
                      {isLoading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <SmsIcon sx={{ mr: 1 }} />
                          Verify &amp; Sign In
                        </>
                      )}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      disabled={isLoading}
                      onClick={() => { setMfaUserId(null); setMfaCode(''); setError(''); }}
                    >
                      Back to sign in
                    </Button>
                  </Box>
                </form>
              ) : (
                <form onSubmit={handleLogin} style={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address"
                      disabled={isLoading}
                      required
                    />
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      disabled={isLoading}
                      required
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      type="submit"
                      disabled={isLoading}
                      sx={{
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: '1rem',
                        '&:disabled': { opacity: 0.6 },
                      }}
                    >
                      {isLoading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Signing in...
                        </>
                      ) : (
                        <>
                          <LoginIcon sx={{ mr: 1 }} />
                          Sign In
                        </>
                      )}
                    </Button>
                  </Box>
                </form>
              )}

              {!mfaUserId && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3 }}>
                  Don't have an account?{' '}
                  <Link component={RouterLink} to="/register" sx={{ fontWeight: 600 }}>
                    Sign up
                  </Link>
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
